import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import styled, { css } from 'styled-components'
import { SectionHeader, SectionHeaderWrapper } from 'features/bpDetails/BpDetails'
import { PRIVATE_PATHS, redirect } from 'utils/paths'
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined'
import Collapse from '@mui/material/Collapse'
import { isNotNilOrEmpty } from 'utils/ramda'
import AnalyticsFolderProgressBar, { getTemplatePercentage } from 'features/bpDetails/Components/AnalyticsFolderProgressBar'
import Modal from 'components/atoms/Modal'
import { ReactComponent as AnalyticsIcon } from 'assets/images/analytics.svg'
import { PieChart } from 'react-minimal-pie-chart'
import { useParams, useLocation } from 'react-router'
import { useMediaQuery } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import useBodyScrollLock from 'hooks/useBodyScrollLock'
import ChecklistRoundedIcon from '@mui/icons-material/ChecklistRounded'
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded'
import PendingActionsRoundedIcon from '@mui/icons-material/PendingActionsRounded'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import ViewModuleOutlinedIcon from '@mui/icons-material/ViewModuleOutlined'
import ViewListOutlinedIcon from '@mui/icons-material/ViewListOutlined'
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import LaunchIcon from '@mui/icons-material/Launch'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import Tooltip from '@mui/material/Tooltip'
import { useSelector } from 'react-redux'
import { getCurrentUser } from 'features/auth/ducks/selectors'
import { getSelectedBp } from 'features/bpDetails/ducks/selectors'
import { selectBpFilesList } from 'ducks/files/selectors'
import { canEditBp } from 'utils/user'
import { getTemplateProvenanceLabel } from 'features/analytics/utils/templateProvenance'
import { scoreColour } from 'features/dashboard/charts/scoreColour'

const getColorByPercentage = scoreColour

const getChecklistStatusLabel = percentage => {
  if (percentage === 100) return 'Completed'
  if (percentage >= 75) return 'Almost done'
  if (percentage >= 50) return 'In progress'
  if (percentage >= 20) return 'Needs work'
  return 'Attention'
}

const AnalyticsBox = ({ bp, isRightPanelExpanded = true }) => {
  const { id } = useParams()
  const location = useLocation()
  const { pathname } = location
  const currentUser = useSelector(getCurrentUser)
  const bpFiles = useSelector(selectBpFilesList)
  const canEdit = canEditBp(currentUser, bp)
  const isMobile = useMediaQuery('(max-width:768px)')
  const [open, setOpen] = useState(!isMobile)
  const [selectedSegmentIndex, setSelectedSegmentIndex] = useState(null)
  const [selectedDesktopTemplateId, setSelectedDesktopTemplateId] = useState(null)
  const [desktopDetailsOpen, setDesktopDetailsOpen] = useState(false)
  const [activeFilter, setActiveFilter] = useState('all')
  const [desktopLayoutVariant, setDesktopLayoutVariant] = useState('cards')
  const checklistCardRefs = useRef({})

  useBodyScrollLock(isMobile && selectedSegmentIndex !== null)

  const redirectPath = useMemo(() => {
    if (pathname.includes('/drafts/')) {
      return PRIVATE_PATHS.drafts
    } else if (pathname.includes('/favourites/')) {
      return PRIVATE_PATHS.savedPassports
    } else {
      return PRIVATE_PATHS.bpList
    }
  }, [pathname])

  const templatesSortedByType = useMemo(() => {
    const checklistTemplates = bp.checklistTemplates || bp.checklist_templates || []
    return isNotNilOrEmpty(checklistTemplates)
      ? [
        ...checklistTemplates.filter(template => template.type === 'default'),
        ...checklistTemplates.filter(template => template.type === 'custom')
      ]
      : []
  }, [bp])

  const overallPieChartData = useMemo(() => {
    if (!isNotNilOrEmpty(templatesSortedByType)) {
      return {
        total: 0,
        completed: 0,
        chartData: []
      }
    }

    const total = templatesSortedByType.length
    const completed = templatesSortedByType.filter(t => getTemplatePercentage(t, bp, bpFiles) === 100).length

    const chartData = templatesSortedByType.map((template, index) => {
      const percentage = getTemplatePercentage(template, bp, bpFiles)
      const baseColor = getColorByPercentage(percentage)

      return {
        title: template.name || `Analytics ${index + 1}`,
        value: 1, // Each segment represents one analytics
        color: baseColor,
        percentage: percentage,
        templateId: template.id,
        templateIndex: index
      }
    })

    return {
      total,
      completed,
      chartData
    }
  }, [bp, bpFiles, templatesSortedByType])

  const checklistSummary = useMemo(() => {
    const total = templatesSortedByType.length
    const completed = templatesSortedByType.filter(
      template => getTemplatePercentage(template, bp, bpFiles) === 100
    ).length
    const attention = templatesSortedByType.filter(
      template => getTemplatePercentage(template, bp, bpFiles) < 50
    ).length
    const inProgress = Math.max(total - completed, 0)

    return {
      total,
      completed,
      inProgress,
      attention
    }
  }, [bp, bpFiles, templatesSortedByType])

  const visibleTemplates = useMemo(() => {
    let filtered
    switch (activeFilter) {
      case 'completed':
        filtered = templatesSortedByType.filter(
          template => getTemplatePercentage(template, bp, bpFiles) === 100
        )
        break
      case 'progress':
        filtered = templatesSortedByType.filter(template => {
          const percentage = getTemplatePercentage(template, bp, bpFiles)
          return percentage > 0 && percentage < 100
        })
        break
      case 'attention':
        filtered = templatesSortedByType.filter(
          template => getTemplatePercentage(template, bp, bpFiles) < 50
        )
        break
      default:
        filtered = templatesSortedByType
    }
    return filtered
  }, [activeFilter, bp, bpFiles, templatesSortedByType])

  const selectedDesktopTemplate = useMemo(() => {
    if (visibleTemplates.length === 0) return null

    return (
      visibleTemplates.find(
        template => String(template.id) === String(selectedDesktopTemplateId)
      ) || visibleTemplates[0]
    )
  }, [selectedDesktopTemplateId, visibleTemplates])

  useEffect(() => {
    if (visibleTemplates.length === 0) {
      setSelectedDesktopTemplateId(null)
      return
    }

    const stillVisible = visibleTemplates.some(
      template => String(template.id) === String(selectedDesktopTemplateId)
    )

    if (!stillVisible) {
      setSelectedDesktopTemplateId(visibleTemplates[0].id)
    }
  }, [selectedDesktopTemplateId, visibleTemplates])

  useEffect(() => {
    if (!selectedDesktopTemplateId) return

    const targetCard =
      checklistCardRefs.current[String(selectedDesktopTemplateId)]

    if (targetCard && typeof targetCard.scrollIntoView === 'function') {
      targetCard.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center'
      })
    }
  }, [selectedDesktopTemplateId])

  const handleSegmentClick = useCallback((event, segmentIndex) => {
    event.stopPropagation()

    setSelectedSegmentIndex(prev => {
      const newValue = prev === segmentIndex ? null : segmentIndex
      if (newValue === null) {
        setOpen(false)
      } else {
        setOpen(true)
      }
      return newValue
    })
  }, [])

  const handleDesktopSegmentClick = useCallback((event, segmentIndex) => {
    event.stopPropagation()

    const segment = overallPieChartData.chartData[segmentIndex]
    if (!segment?.templateId) return

    setSelectedDesktopTemplateId(segment.templateId)
  }, [overallPieChartData.chartData])

  const handleCloseSelection = () => {
    setSelectedSegmentIndex(null)
    setOpen(false)
  }

  const handleToggleOpen = useCallback(() => {
    setOpen(prev => !prev)
  }, [])

  const handlePrevChecklist = () => {
    if (!overallPieChartData.chartData.length) return
    setSelectedSegmentIndex(prev => {
      const current = prev == null ? 0 : prev
      const nextIndex = (current - 1 + overallPieChartData.chartData.length) % overallPieChartData.chartData.length
      setOpen(true)
      return nextIndex
    })
  }

  const handleNextChecklist = () => {
    if (!overallPieChartData.chartData.length) return
    setSelectedSegmentIndex(prev => {
      const current = prev == null ? 0 : prev
      const nextIndex = (current + 1) % overallPieChartData.chartData.length
      setOpen(true)
      return nextIndex
    })
  }

  const segmentsStyle = useMemo(() => {
    return (index) => {
      const isSelected = selectedSegmentIndex === index
      const hasSelection = selectedSegmentIndex !== null
      return {
        cursor: 'pointer',
        transition: 'all 0.3s',
        opacity: isSelected ? 1 : (hasSelection ? 0.3 : 1),
      }
    }
  }, [selectedSegmentIndex, overallPieChartData.chartData.length])

  const segmentsShift = useMemo(() => {
    return (index) => {
      return selectedSegmentIndex === index ? 2 : 0
    }
  }, [selectedSegmentIndex])

  const selectedTemplateName =
    selectedSegmentIndex !== null &&
    overallPieChartData.chartData[selectedSegmentIndex]
      ? overallPieChartData.chartData[selectedSegmentIndex].title
      : null

  const headerTitle = selectedTemplateName || 'Checklists'

  return (
    <AnalyticsBoxWrapper isRightPanelExpanded={isRightPanelExpanded}>
      <ClickableSectionHeaderWrapper
        withBorder={open}
        noMargin
        onClick={handleToggleOpen}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            handleToggleOpen()
          }
        }}
      >
        <SectionHeaderWithChart>
          <HeaderTitleRow>
            <SectionHeader className="checklists-title" withPointer>
              {headerTitle}
            </SectionHeader>
            {!isMobile && (
              <HeaderLayoutToggle
                onClick={event => {
                  event.stopPropagation()
                  setDesktopLayoutVariant(prev =>
                    prev === 'cards' ? 'simple' : 'cards'
                  )
                }}
                title={
                  desktopLayoutVariant === 'cards'
                    ? 'Switch to vertical view'
                    : 'Switch to horizontal view'
                }
              >
                <HeaderLayoutIcon $active={desktopLayoutVariant === 'simple'}>
                  <ViewListOutlinedIcon fontSize='small' />
                </HeaderLayoutIcon>
                <HeaderToggleRail $active={desktopLayoutVariant === 'cards'}>
                  <HeaderToggleThumb $active={desktopLayoutVariant === 'cards'} />
                </HeaderToggleRail>
                <HeaderLayoutIcon $active={desktopLayoutVariant === 'cards'}>
                  <ViewModuleOutlinedIcon fontSize='small' />
                </HeaderLayoutIcon>
              </HeaderLayoutToggle>
            )}
          </HeaderTitleRow>
          {overallPieChartData.chartData.length > 0 && (
            <HeaderPieChartWrapper onClick={(event) => event.stopPropagation()}>
              <PieChart
                animate
                lineWidth={12}
                data={overallPieChartData.chartData}
                onClick={handleSegmentClick}
                viewBoxSize={[100, 100]}
                center={[50, 50]}
                segmentsStyle={segmentsStyle}
                segmentsShift={segmentsShift}
              />
              <HeaderProgressCount>
                {selectedSegmentIndex !== null && overallPieChartData.chartData[selectedSegmentIndex] ? (
                  <HeaderCompletedCount>
                    {overallPieChartData.chartData[selectedSegmentIndex].percentage}%
                  </HeaderCompletedCount>
                ) : (
                  <HeaderCompletedCount>
                    {overallPieChartData.completed}/{overallPieChartData.total}
                  </HeaderCompletedCount>
                )}
              </HeaderProgressCount>
            </HeaderPieChartWrapper>
          )}
          {isMobile && selectedSegmentIndex !== null && overallPieChartData.chartData.length > 0 && (
            <MobileControlsWrapper onClick={(e) => e.stopPropagation()}>
              <MobileControls>
                <ControlButton onClick={(e) => { e.stopPropagation(); handlePrevChecklist(); }}>
                  <ArrowBackIosNewIcon fontSize="small" />
                </ControlButton>
                <Dots>
                  {overallPieChartData.chartData.map((_, index) => (
                    <Dot key={`dot-${index}`} $active={index === selectedSegmentIndex} />
                  ))}
                </Dots>
                <ControlButton onClick={(e) => { e.stopPropagation(); handleNextChecklist(); }}>
                  <ArrowForwardIosIcon fontSize="small" />
                </ControlButton>
              </MobileControls>
            </MobileControlsWrapper>
          )}
        </SectionHeaderWithChart>
        <ActionsWrapper onClick={(event) => event.stopPropagation()}>
          <CollapseIcon
            open={open}
            onClick={handleToggleOpen}
          />
        </ActionsWrapper>
      </ClickableSectionHeaderWrapper>
      <Collapse in={open}>
        <AnalyticsContent open={open}>
          {!isMobile && (
            <OverviewPanel>
              <SummaryGrid>
                <SummaryCard
                  type='button'
                  $tone='all'
                  $active={activeFilter === 'all'}
                  onClick={() => setActiveFilter('all')}
                >
                  <SummaryIconWrap $tone='all'>
                    <ChecklistRoundedIcon fontSize='inherit' />
                  </SummaryIconWrap>
                  <SummaryValue>{checklistSummary.total}</SummaryValue>
                  <SummaryLabel>Total</SummaryLabel>
                </SummaryCard>
                <SummaryCard
                  type='button'
                  $tone='completed'
                  $active={activeFilter === 'completed'}
                  onClick={() => setActiveFilter('completed')}
                >
                  <SummaryIconWrap $tone='completed'>
                    <TaskAltRoundedIcon fontSize='inherit' />
                  </SummaryIconWrap>
                  <SummaryValue>{checklistSummary.completed}</SummaryValue>
                  <SummaryLabel>Completed</SummaryLabel>
                </SummaryCard>
                <SummaryCard
                  type='button'
                  $tone='progress'
                  $active={activeFilter === 'progress'}
                  onClick={() => setActiveFilter('progress')}
                >
                  <SummaryIconWrap $tone='progress'>
                    <PendingActionsRoundedIcon fontSize='inherit' />
                  </SummaryIconWrap>
                  <SummaryValue>{checklistSummary.inProgress}</SummaryValue>
                  <SummaryLabel>In progress</SummaryLabel>
                </SummaryCard>
                <SummaryCard
                  type='button'
                  $tone='attention'
                  $active={activeFilter === 'attention'}
                  onClick={() => setActiveFilter('attention')}
                >
                  <SummaryIconWrap $tone='attention'>
                    <ErrorOutlineRoundedIcon fontSize='inherit' />
                  </SummaryIconWrap>
                  <SummaryValue>{checklistSummary.attention}</SummaryValue>
                  <SummaryLabel>Needs attention</SummaryLabel>
                </SummaryCard>
              </SummaryGrid>

              <FilterBar>
                <FilterChip
                  type='button'
                  $active={activeFilter === 'all'}
                  onClick={() => setActiveFilter('all')}
                >
                  All checklists
                </FilterChip>
                <FilterChip
                  type='button'
                  $active={activeFilter === 'completed'}
                  onClick={() => setActiveFilter('completed')}
                >
                  Completed
                </FilterChip>
                <FilterChip
                  type='button'
                  $active={activeFilter === 'progress'}
                  onClick={() => setActiveFilter('progress')}
                >
                  In progress
                </FilterChip>
                <FilterChip
                  type='button'
                  $active={activeFilter === 'attention'}
                  onClick={() => setActiveFilter('attention')}
                >
                  Needs attention
                </FilterChip>
              </FilterBar>
            </OverviewPanel>
          )}

          <ProgressBarsContainer>
            {visibleTemplates.length > 0 ? (
              <DesktopChecklistWorkspace>
                <CompactChecklistRail>
                  {desktopLayoutVariant === 'simple' ? (
                    <SimpleChecklistList>
                      {visibleTemplates.map(template => {
                        const percentage = getTemplatePercentage(template, bp, bpFiles)

                        return (
                        <SimpleChecklistRow
                          key={`analytic-simple-${template.id}`}
                          ref={node => {
                            checklistCardRefs.current[String(template.id)] = node
                          }}
                          type='button'
                          $active={
                            String(selectedDesktopTemplate?.id) === String(template.id)
                          }
                          onClick={() => {
                            setSelectedDesktopTemplateId(template.id)
                            setDesktopDetailsOpen(true)
                          }}
                        >
                          <SimpleChecklistInfo>
                            <SimpleChecklistName>
                              {template.name}
                            </SimpleChecklistName>
                            <SimpleMetaRow>
                              {template.visibility && (
                                <SimpleMetaBadge>
                                  <GroupsOutlinedIcon fontSize='inherit' />
                                  {template.visibility === 'personal' ? 'Personal' : 'Shared'}
                                </SimpleMetaBadge>
                              )}
                              {(() => {
                                const rawLocked = template?.locked ?? template?.is_locked ?? template?.isLocked ?? template?.pivot?.locked
                                const isLocked = !!rawLocked
                                const creatorId = template?.creator_id || template?.creatorId || template?.creator?.id
                                const isCreator = creatorId && currentUser?.id ? String(creatorId) === String(currentUser.id) : false
                                const isAdmin = currentUser?.type === 'admin'
                                const canEditTemplate = isCreator || isAdmin || !isLocked

                                if (canEditTemplate && template.type !== 'default' && canEdit) {
                                  return (
                                    <SimpleEditBadge
                                      type='button'
                                      onClick={e => {
                                        e.stopPropagation()
                                        redirect(PRIVATE_PATHS.editAnalytics + `#${template.id}`)
                                      }}
                                    >
                                      <LaunchIcon fontSize='inherit' />
                                      Edit template
                                    </SimpleEditBadge>
                                  )
                                }
                                if (isLocked) {
                                  return (
                                    <SimpleMetaBadge $locked>
                                      Non-editable
                                    </SimpleMetaBadge>
                                  )
                                }
                                return null
                              })()}
                              {(() => {
                                const label = getTemplateProvenanceLabel(template)
                                return label ? (
                                  <Tooltip title={label} arrow>
                                    <SimpleMetaInfo>
                                      <InfoOutlinedIcon fontSize='inherit' />
                                    </SimpleMetaInfo>
                                  </Tooltip>
                                ) : null
                              })()}
                            </SimpleMetaRow>
                          </SimpleChecklistInfo>
                          <SimpleProgressWrap>
                            <SimpleProgressTrack>
                              <SimpleProgressFill
                                $progress={percentage}
                                $color={getColorByPercentage(percentage)}
                              />
                            </SimpleProgressTrack>
                          </SimpleProgressWrap>
                          <SimpleChecklistValue>
                            {percentage}%
                          </SimpleChecklistValue>
                        </SimpleChecklistRow>
                        )
                      })}
                    </SimpleChecklistList>
                  ) : (
                    <CompactChecklistList>
                      {visibleTemplates.map(template => {
                        const percentage = getTemplatePercentage(template, bp, bpFiles)

                        return (
                        <CompactChecklistRow
                          key={`analytic-card-${template.id}`}
                          ref={node => {
                            checklistCardRefs.current[String(template.id)] = node
                          }}
                          type='button'
                          $active={
                            String(selectedDesktopTemplate?.id) === String(template.id)
                          }
                          onClick={() => {
                            setSelectedDesktopTemplateId(template.id)
                            setDesktopDetailsOpen(true)
                          }}
                        >
                          <CompactChecklistMain>
                            <CompactCardTop>
                              <CompactChecklistName>
                                {template.name}
                              </CompactChecklistName>
                              <CompactDonutWrap>
                                <PieChart
                                  animate
                                  lineWidth={18}
                                  data={[
                                    {
                                      title: 'Completed',
                                      value: Math.max(percentage, 0),
                                      color: getColorByPercentage(percentage)
                                    },
                                    {
                                      title: 'Remaining',
                                      value: Math.max(100 - percentage, 0),
                                      color: 'rgba(23, 32, 51, 0.08)'
                                    }
                                  ]}
                                  viewBoxSize={[100, 100]}
                                  center={[50, 50]}
                                />
                                <CompactDonutValue>
                                  {percentage}%
                                </CompactDonutValue>
                              </CompactDonutWrap>
                            </CompactCardTop>
                            <CompactChecklistMeta>
                              {template.visibility && (
                                <CompactMetaChip>
                                  {template.visibility === 'personal'
                                    ? 'Personal'
                                    : 'Shared'}
                                </CompactMetaChip>
                              )}
                              {(() => {
                                const rawLocked = template?.locked ?? template?.is_locked ?? template?.isLocked ?? template?.pivot?.locked
                                const isLocked = !!rawLocked
                                const creatorId = template?.creator_id || template?.creatorId || template?.creator?.id
                                const isCreator = creatorId && currentUser?.id ? String(creatorId) === String(currentUser.id) : false
                                const isAdmin = currentUser?.type === 'admin'
                                const canEditTemplate = isCreator || isAdmin || !isLocked

                                if (canEditTemplate && template.type !== 'default' && canEdit) {
                                  return (
                                    <CompactEditChip
                                      type='button'
                                      onClick={e => {
                                        e.stopPropagation()
                                        redirect(PRIVATE_PATHS.editAnalytics + `#${template.id}`)
                                      }}
                                    >
                                      <LaunchIcon fontSize='inherit' />
                                      Edit template
                                    </CompactEditChip>
                                  )
                                }
                                if (isLocked) {
                                  return (
                                    <CompactMetaChip $tone='locked'>
                                      Non-editable
                                    </CompactMetaChip>
                                  )
                                }
                                return null
                              })()}
                              {(() => {
                                const label = getTemplateProvenanceLabel(template)
                                return label ? (
                                  <Tooltip title={label} arrow>
                                    <SimpleMetaInfo>
                                      <InfoOutlinedIcon fontSize='inherit' />
                                    </SimpleMetaInfo>
                                  </Tooltip>
                                ) : null
                              })()}
                            </CompactChecklistMeta>
                            <CompactCardFooter>
                              <CompactFooterLabel>Completion</CompactFooterLabel>
                              <CompactFooterValue>
                                {percentage}%
                              </CompactFooterValue>
                            </CompactCardFooter>
                          </CompactChecklistMain>
                        </CompactChecklistRow>
                        )
                      })}
                    </CompactChecklistList>
                  )}
                </CompactChecklistRail>
              </DesktopChecklistWorkspace>
            ) : (
                <EmptyChecklistState>
                  <EmptyChecklistTitle>
                    No checklists in this view
                  </EmptyChecklistTitle>
                  <EmptyChecklistText>
                    Try a different filter to see the rest of the templates for
                    this Building Passport.
                  </EmptyChecklistText>
                </EmptyChecklistState>
            )}
          </ProgressBarsContainer>

          <MobileTemplateDetails>
            {selectedSegmentIndex !== null && overallPieChartData.chartData[selectedSegmentIndex] && templatesSortedByType[selectedSegmentIndex] && (
              <>
                <MobileDetailsOverlay onClick={handleCloseSelection} />
                <MobileDetailsPanel onClick={(e) => e.stopPropagation()}>
                  <MobileDetailsHeader>
                    <span>{headerTitle}</span>
                    <CloseDetailsButton onClick={handleCloseSelection}>
                      <CloseIcon fontSize="small" />
                    </CloseDetailsButton>
                  </MobileDetailsHeader>
                  <AnalyticsFolderProgressBar
                    key={`mobile-template-${templatesSortedByType[selectedSegmentIndex].id}`}
                    template={templatesSortedByType[selectedSegmentIndex]}
                    redirectPath={redirectPath}
                    showDetailsOnly={true}
                  />
                  <MobileDots>
                    {overallPieChartData.chartData.map((_, index) => (
                      <Dot key={`detail-dot-${index}`} $active={index === selectedSegmentIndex} />
                    ))}
                  </MobileDots>
                </MobileDetailsPanel>
              </>
            )}
          </MobileTemplateDetails>
        </AnalyticsContent>
        {open && selectedSegmentIndex !== null && (
          <RedirectButtonWrapper>
            <RedirectButton
              onClick={() => redirect(PRIVATE_PATHS.analytics)}
            >
              <AnalyticsIcon /> go to checklists overview
            </RedirectButton>
          </RedirectButtonWrapper>
        )}
      </Collapse>
      <Modal
        open={!isMobile && desktopDetailsOpen && Boolean(selectedDesktopTemplate)}
        title={selectedDesktopTemplate?.name || 'Checklist details'}
        onClose={() => setDesktopDetailsOpen(false)}
        minWidthDesktop='960px'
        maxWidthDesktop='1120px'
      >
        {selectedDesktopTemplate && (
          <DesktopModalContent>
            <DesktopModalMeta>
              <ChecklistDetailsEyebrow>Checklist details</ChecklistDetailsEyebrow>
              <ChecklistDetailsStageBadge>
                {getTemplatePercentage(selectedDesktopTemplate, bp, bpFiles)}% complete
              </ChecklistDetailsStageBadge>
            </DesktopModalMeta>
            <AnalyticsFolderProgressBar
              key={`desktop-template-modal-${selectedDesktopTemplate.id}`}
              template={selectedDesktopTemplate}
              redirectPath={redirectPath}
              showDetailsOnly
            />
          </DesktopModalContent>
        )}
      </Modal>
    </AnalyticsBoxWrapper>
  )
}

export default AnalyticsBox

const AnalyticsBoxWrapper = styled.div`
  box-sizing: border-box;
  background-color: ${({ theme }) => theme.colors.white};
  padding: 0 10px 10px;
  box-shadow: none;
  border-radius: 4px;
  width: 100%;
  margin-top: 0;
  position: relative;
`

const ClickableSectionHeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0;
  cursor: pointer;

  ${({ withBorder, theme }) =>
    withBorder &&
    css`
      padding-bottom: 12px;
      border-bottom: 1px solid ${theme.colors.lightGrey};
    `}
`

export const RedirectButton = styled.div`
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
  font-size: 11px;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: .3px;
  display: flex;
  align-items: center;

  svg {
    fill: ${({ theme }) => theme.colors.primary};
    margin-right: 5px;
    padding-bottom: 2px;
  }
`

const CollapseIcon = styled(KeyboardArrowDownOutlinedIcon)`
  transition: all .3s;
  cursor: pointer;
  transform: rotate(${({ open }) => open ? '180deg': '0'});
  margin-left: 10px;

  @media (max-width: 768px) {
    display: none;
  }
`

const ActionsWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-left: auto;
  cursor: pointer;

  @media (max-width: 768px) {
    display: none;
  }
`

const AnalyticsContent = styled.div`
  padding: 0;
  transition: all .3s;

  ${({ open }) => open && css`
    padding-top: 20px;
  `}
`

const ProgressBarsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 8px 0 12px;

  @media (max-width: 768px) {
    display: none;
  }
`

const OverviewPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 10px;
`

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
`

const getSummaryTone = tone => {
  switch (tone) {
    case 'completed':
      return {
        border: 'rgba(61, 179, 122, 0.22)',
        bg: 'rgba(61, 179, 122, 0.08)',
        icon: '#208553'
      }
    case 'progress':
      return {
        border: 'rgba(255, 175, 126, 0.28)',
        bg: 'rgba(255, 175, 126, 0.12)',
        icon: '#b27300'
      }
    case 'attention':
      return {
        border: 'rgba(204, 34, 0, 0.18)',
        bg: 'rgba(204, 34, 0, 0.08)',
        icon: '#b00020'
      }
    default:
      return {
        border: 'rgba(23, 32, 51, 0.08)',
        bg: 'rgba(23, 32, 51, 0.04)',
        icon: '#172033'
      }
  }
}

const SummaryCard = styled.button`
  text-align: left;
  border-radius: 10px;
  border: 1px solid ${({ $tone }) => getSummaryTone($tone).border};
  background: ${({ $tone, $active }) =>
    $active ? getSummaryTone($tone).bg : '#fff'};
  padding: 12px 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: none;

  &:hover {
    border-color: ${({ $tone }) => getSummaryTone($tone).icon};
  }
`

const SummaryIconWrap = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: ${({ $tone }) => getSummaryTone($tone).bg};
  color: ${({ $tone }) => getSummaryTone($tone).icon};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
`

const SummaryValue = styled.div`
  margin-top: 12px;
  font-size: 22px;
  line-height: 1;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
`

const SummaryLabel = styled.div`
  margin-top: 4px;
  font-size: 12px;
  color: #5f6677;
`

const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
`

const HeaderTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const FilterChip = styled.button`
  border: 1px solid
    ${({ $active }) => ($active ? 'rgba(51, 190, 204, 0.24)' : 'rgba(23, 32, 51, 0.08)')};
  background: ${({ $active }) => ($active ? 'rgba(51, 190, 204, 0.09)' : '#fff')};
  color: ${({ $active }) => ($active ? '#17818a' : '#4f586a')};
  border-radius: 8px;
  padding: 7px 12px;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: rgba(51, 190, 204, 0.2);
    color: #17818a;
  }
`

const HeaderLayoutToggle = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  flex-shrink: 0;
`

const HeaderLayoutIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ $active }) => ($active ? '#17818a' : '#697285')};
  transition: color 0.15s ease;
`

const HeaderToggleRail = styled.span`
  position: relative;
  width: 38px;
  height: 20px;
  border-radius: 999px;
  background: ${({ $active }) =>
    $active ? 'rgba(51, 190, 204, 0.22)' : 'rgba(23, 32, 51, 0.12)'};
  border: 1px solid
    ${({ $active }) =>
      $active ? 'rgba(51, 190, 204, 0.28)' : 'rgba(23, 32, 51, 0.1)'};
  transition: all 0.15s ease;
`

const HeaderToggleThumb = styled.span`
  position: absolute;
  top: 2px;
  left: ${({ $active }) => ($active ? '20px' : '2px')};
  width: 14px;
  height: 14px;
  border-radius: 999px;
  background: ${({ $active }) => ($active ? '#17818a' : '#697285')};
  transition: left 0.15s ease, background 0.15s ease;
`

const DesktopChecklistWorkspace = styled.div`
  display: block;
`

const CompactChecklistRail = styled.div`
  min-width: 0;
`

const CompactChecklistList = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(210px, 226px);
  gap: 10px;
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 4px;
  scroll-snap-type: x proximity;

  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(23, 32, 51, 0.16);
    border-radius: 999px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(23, 32, 51, 0.05);
    border-radius: 999px;
  }
`

const SimpleChecklistList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`

const SimpleChecklistRow = styled.button`
  width: 100%;
  display: grid;
  grid-template-columns: minmax(180px, 280px) minmax(0, 1fr) auto;
  gap: 20px;
  align-items: center;
  border-radius: 0;
  border: none;
  border-bottom: 1px solid rgba(23, 32, 51, 0.06);
  background: ${({ $active }) =>
    $active ? 'rgba(51, 190, 204, 0.05)' : 'transparent'};
  padding: 14px 8px;
  cursor: pointer;
  text-align: left;
  transition: all 0.18s ease;

  &:hover {
    background: rgba(51, 190, 204, 0.04);
  }

  &:last-child {
    border-bottom: none;
  }
`

const SimpleChecklistInfo = styled.div`
  min-width: 0;
`

const SimpleMetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
  flex-wrap: wrap;
`

const SimpleMetaBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 11px;
  font-weight: 600;
  color: ${({ $locked }) => $locked ? '#b43f2d' : '#697285'};
  background: ${({ $locked }) => $locked ? 'rgba(180, 63, 45, 0.1)' : 'rgba(23, 32, 51, 0.06)'};
  border-radius: 8px;
  padding: 3px 8px;
  white-space: nowrap;
`

const SimpleEditBadge = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 11px;
  font-weight: 600;
  color: #17818a;
  background: rgba(51, 190, 204, 0.1);
  border: none;
  border-radius: 8px;
  padding: 3px 8px;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: rgba(51, 190, 204, 0.2);
  }
`

const SimpleMetaInfo = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 16px;
  color: #8a91a1;
  cursor: pointer;
`

const SimpleChecklistName = styled.div`
  font-size: 15px;
  line-height: 1.25;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const SimpleProgressWrap = styled.div`
  min-width: 0;
`

const SimpleProgressTrack = styled.div`
  width: 100%;
  height: 8px;
  border-radius: 6px;
  background: rgba(23, 32, 51, 0.08);
  overflow: hidden;
`

const SimpleProgressFill = styled.div`
  width: ${({ $progress }) => `${Math.min(Math.max($progress, 0), 100)}%`};
  height: 100%;
  border-radius: inherit;
  background: ${({ $color }) => $color};
`

const SimpleChecklistValue = styled.div`
  font-size: 15px;
  line-height: 1;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
  white-space: nowrap;
`

const CompactChecklistRow = styled.button`
  width: 100%;
  display: block;
  border-radius: 10px;
  border: 1px solid
    ${({ $active }) =>
      $active ? 'rgba(51, 190, 204, 0.26)' : 'rgba(23, 32, 51, 0.08)'};
  background: ${({ $active }) =>
    $active
      ? 'linear-gradient(180deg, rgba(51, 190, 204, 0.08) 0%, rgba(255, 255, 255, 0.98) 100%)'
      : '#fff'};
  box-shadow: none;
  padding: 14px;
  cursor: pointer;
  text-align: left;
  transition: all 0.18s ease;
  scroll-snap-align: start;
  position: relative;
  animation: none;

  &:hover {
    border-color: rgba(51, 190, 204, 0.2);
  }
`

const CompactChecklistMain = styled.div`
  min-width: 0;
`

const CompactCardTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
`

const CompactChecklistName = styled.div`
  font-size: 14px;
  line-height: 1.2;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

const CompactChecklistMeta = styled.div`
  margin-top: 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

const CompactEditChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  border-radius: 999px;
  padding: 5px 10px;
  background: rgba(51, 190, 204, 0.1);
  color: #17818a;
  border: none;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;

  &:hover {
    background: rgba(51, 190, 204, 0.2);
  }
`

const CompactMetaChip = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 5px 10px;
  background: ${({ $tone }) =>
    $tone === 'locked' ? 'rgba(180, 63, 45, 0.1)' :
    $tone === 'status' ? 'rgba(51, 190, 204, 0.1)' : 'rgba(23, 32, 51, 0.06)'};
  color: ${({ $tone }) =>
    $tone === 'locked' ? '#b43f2d' :
    $tone === 'status' ? '#17818a' : '#596275'};
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
`

const CompactDonutWrap = styled.div`
  position: relative;
  width: 62px;
  height: 62px;
  flex-shrink: 0;
`

const CompactDonutValue = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
`

const CompactCardFooter = styled.div`
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid rgba(23, 32, 51, 0.07);
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
`

const CompactFooterLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #8a91a1;
`

const CompactFooterValue = styled.div`
  font-size: 18px;
  line-height: 1;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
`

const DesktopModalContent = styled.div`
  min-width: 0;
`

const ChecklistDetailsEyebrow = styled.div`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #17818a;
  margin-bottom: 6px;
`

const DesktopModalMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
`

const ChecklistDetailsStageBadge = styled.div`
  border-radius: 999px;
  padding: 7px 12px;
  background: rgba(51, 190, 204, 0.1);
  color: #17818a;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
`

const EmptyChecklistState = styled.div`
  border: 1px dashed rgba(23, 32, 51, 0.14);
  border-radius: 10px;
  padding: 30px 24px;
  background: rgba(247, 250, 252, 0.8);
  text-align: center;
`

const EmptyChecklistTitle = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
`

const EmptyChecklistText = styled.div`
  margin-top: 8px;
  color: #697285;
  font-size: 14px;
  line-height: 1.6;
`

const HeaderPieChartWrapper = styled.div`
  display: none;
  cursor: pointer;

  @media (max-width: 768px) {
    display: flex;
    position: relative;
    width: 64px;
    height: 64px;
    align-items: center;
    justify-content: center;
    overflow: visible;
    margin: 0 auto;
  }
`

const HeaderProgressCount = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`

const HeaderCompletedCount = styled.div`
  font-size: 18px;
  font-weight: bold;
  color: ${({ theme, $inverse }) =>
    $inverse ? theme.colors.white : theme.colors.haiti};

  @media (max-width: 768px) {
    font-size: 12px;
  }
`

const RedirectButtonWrapper = styled.div`
  margin-top: 20px;
  width: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
`

const MobileTemplateDetails = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: block;
    margin-top: 20px;
  }
`

const MobileDetailsOverlay = styled.div`
  @media (max-width: 768px) {
    position: fixed;
    inset: 0;
    background: transparent;
    z-index: 4;
  }
`

const MobileDetailsPanel = styled.div`
  @media (max-width: 768px) {
    position: relative;
    z-index: 5;
  }
`

const MobileControlsWrapper = styled.div`
  @media (max-width: 768px) {
    position: relative;
    z-index: 6;
  }
`

const MobileControls = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 6px;
  }
`

const ControlButton = styled.button`
  border: 1px solid ${({ theme }) => theme.colors.grey[400]};
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.haiti};
  width: 30px;
  height: 30px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`

const Dots = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
`

const Dot = styled.div`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${({ $active, theme }) => ($active ? theme.colors.primary : 'rgba(169,169,169,0.34)')};
  opacity: 1;
  border: ${({ $active }) => ($active ? 'none' : '1px solid rgba(169,169,169,0.34)')};
  transform: ${({ $active }) => ($active ? 'scale(1.1)' : 'scale(1)')};
`

const MobileDetailsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  font-weight: 700;
`

const CloseDetailsButton = styled.button`
  border: 1px solid ${({ theme }) => theme.colors.grey[400]};
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.haiti};
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`

const MobileDots = styled.div`
  display: flex;
  justify-content: center;
  gap: 6px;
  margin-top: 12px;
`


const SectionHeaderWithChart = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 15px;
    width: 100%;
    text-align: center;

    .checklists-title {
      order: -1;
      margin-bottom: 0;
      text-align: center;
      width: 100%;
      margin-left: 0;
    }
  }
`
